export function formatDate(date: Date | undefined) {
  if (date) {
    return date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear();
  }
  return "--";
}

/** Zero-padded dd/mm/yyyy — used where alignment matters (e.g. the sidebar). */
export function formatDatePadded(date: Date | undefined) {
  if (date) {
    return date.getDate().toString().padStart(2, '0') + '/'
      + (date.getMonth() + 1).toString().padStart(2, '0') + '/'
      + date.getFullYear();
  }
  return "--";
}

export function formatDateGSheet(date: Date | undefined) {
  if (date) {
    return date.getFullYear() + '-' + (date.getMonth() + 1).toString().padStart(2, "0") + '-' + date.getDate().toString().padStart(2, "0");
  }
  return "--";
}

/**
 * YYYY-MM-DD for the day AFTER `date` (local time) — the morning after an
 * overnight stay (D+1), used by the noon-conflict checks.
 */
export function nextDayStr(date: Date): string {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return formatDateGSheet(d);
}

/**
 * Year + 0-indexed month of the chronologically earliest `YYYY-MM-DD` string in
 * `dates`, or `null` when the list is empty. The month is 0-indexed to match
 * `Date.getMonth()` and Vuetify's `<v-date-picker>` `month` prop.
 *
 * Parsed by slicing the string numerically rather than `new Date(str)`: the
 * latter parses a bare `YYYY-MM-DD` as UTC midnight, so a first-of-month date
 * would report the previous month in any UTC-negative timezone. Lexicographic
 * order equals chronological order for strict zero-padded ISO date strings, so a
 * plain string `min` finds the earliest day without parsing every entry.
 */
export function firstAvailableMonth(
  dates: string[],
): { year: number; month: number } | null {
  if (dates.length === 0) return null;
  let earliest = dates[0];
  for (const d of dates) {
    if (d < earliest) earliest = d;
  }
  return {
    year: Number(earliest.slice(0, 4)),
    month: Number(earliest.slice(5, 7)) - 1,
  };
}

/**
 * Monday-first week rows for a month: each cell a local `YYYY-MM-DD` string,
 * `null` outside the month. `month` is 0-indexed to match `Date.getMonth()`.
 * Dates are built from parts (never `new Date('YYYY-MM-DD')`) to avoid the
 * UTC-midnight off-by-one — see `firstAvailableMonth` above.
 */
export function monthGrid(year: number, month: number): (string | null)[][] {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const mondayOffset = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0 … Sun=6
  const cells: (string | null)[] = Array(mondayOffset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(formatDateGSheet(new Date(year, month, d)));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}
